/* A1 Chatbot v9 ("Spinny") - FAQ matching + mascot state machine. Vanilla JS, no dependencies.
 * He parks bottom-right, can wander (Playful), scurries to page elements, points, and does a
 * pretend click (glow + ripple). Nothing on the page is ever actually clicked.
 * Data: assets/a1-chatbot-faq.json (records with "sample": true are hidden on the storefront)
 * Products: Shopify predictive search (/search/suggest.json) when the FAQ has no answer
 * Poses: mascot-idle / mascot-scurry / mascot-point (.webp) - all drawn facing RIGHT
 * Holidays: costume overlays by date (theme setting "Holiday costumes"; preview ?a1chat_holiday=halloween)
 * Events: dataLayer chatbot_open | chatbot_question | chatbot_fallback | chatbot_thumbs
 */
(function () {
  'use strict';

  var mq = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var REDUCED = mq('(prefers-reduced-motion: reduce)');
  var ROAM_DELAY = 25000; // ms of inactivity between strolls (playful mode only)
  var FIRST_ROAM_MS = 15000; // first stroll after the page loads
  var NUDGE_DELAY = 20000; // one-time "Questions? Ask me!" per session
  var WAVE_EVERY = 40000; // a little wave in place every 40-55s while he's parked (no bubble)
  var TRAVEL_MS = 1600; // must match the CSS transition on .a1-chat-mascot
  var POINT_HOLD = 2800; // ms he holds a point (includes the pretend click)
  var TAP_AT = 420; // ms after arriving that he taps the element
  var GLOW_MS = 3200; // how long the tapped element keeps its glow
  var HAND_Y = 0.394; // fingertip height as a fraction of the point pose (row 104 of 264)
  var TYPING_MS = 600;
  var THRESHOLD = 0.35; // min score for a confident answer
  var DISMISS_KEY = 'a1chat_dismissed_v1';
  var NUDGE_KEY = 'a1chat_nudged_v1';
  var HASH_PREFIX = '#a1-point=';
  var PHONE = '443-552-7338';
  var EMAIL = 'a1dentalsupply@gmail.com';
  var HOURS_URL = '/pages/contact';
  var TZ = 'America/New_York';
  var DAY = 864e5;

  function isMobile() { return mq('(max-width: 480px)'); } // checked live, not just at load
  function now() { return window.A1_CHAT_NOW ? new Date(window.A1_CHAT_NOW) : new Date(); } // QA hook

  function ss(k, v) { // sessionStorage that never throws (private mode, blocked storage)
    try {
      if (v === undefined) return window.sessionStorage.getItem(k);
      window.sessionStorage.setItem(k, v);
    } catch (e) { return null; }
  }

  /* ---------- matching ----------
     Words are cleaned, stop words dropped, then stemmed (handpieces -> handpiece, shipping -> ship).
     A question variant scores by how much of it the visitor's words cover AND how much of the
     visitor's words it explains, so one shared word can't drag in an unrelated answer. */
  var STOP = {};
  ('the a an is are do does did you your i my me how what when where which can could of for to in on it its ' +
   'and or we us our with by be at as this that there their they them from about many not no if any ' +
   'have has had get got one will would should want need please hi hello hey im ive id ill ' +
   'whats wheres hows whos whens thats theres dont doesnt didnt cant isnt arent wont youre youve yall ' +
   'so just also some was were am been being show find looking look tell sell carry offer anyone ' +
   'anything something thank thanks ok okay yes yeah really very')
    .split(' ').forEach(function (w) { STOP[w] = 1; });
  // domain words that appear in almost every question; ignored unless they're all the visitor typed
  var WEAK = {};
  'handpiece dental dentist a1 office practice item product thing help question know like'.split(' ')
    .forEach(function (w) { WEAK[w] = 1; });
  var SYN = {
    much: 'cost', fix: 'repair', fixing: 'repair', lube: 'lubric', lub: 'lubric', oil: 'lubric',
    payment: 'pay', delivery: 'ship', deliver: 'ship', shipment: 'ship', postage: 'ship',
    coupon: 'discount', promo: 'discount', deal: 'discount', sale: 'discount',
    overseas: 'international', abroad: 'international', worldwide: 'international', outside: 'international',
    canada: 'international', uk: 'international', europe: 'international', australia: 'international',
    mexico: 'international', foreign: 'international', country: 'international', countrie: 'international'
  };

  function undouble(s) { return /([^aeioulsz])\1$/.test(s) ? s.slice(0, -1) : s; }
  function stem(t) {
    if (t.length <= 3) return t;
    if (/ies$/.test(t) && t.length > 4) return t.slice(0, -3) + 'y';
    if (/(ss|us|is)$/.test(t)) return t;
    if (/(s|x|z|ch|sh)es$/.test(t)) return t.slice(0, -2);
    if (/ing$/.test(t) && t.length >= 6) return undouble(t.slice(0, -3));
    if (/ed$/.test(t) && t.length >= 5) return undouble(t.slice(0, -2));
    if (/s$/.test(t)) return t.slice(0, -1);
    return t;
  }

  function tokens(s) {
    var out = [];
    (s || '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).forEach(function (t) {
      if (!t || STOP[t]) return;
      if (t.length < 2 && !/\d/.test(t)) return;
      t = stem(t);
      if (t.indexOf('lubric') === 0) t = 'lubric';
      t = SYN[t] || t;
      if (out.indexOf(t) === -1) out.push(t);
    });
    if (!out.length) { // all small words ("where are you", "about us"): compare them as typed
      out = (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
    }
    var strong = out.filter(function (t) { return !WEAK[t]; });
    return strong.length ? strong : out;
  }

  function tokMatch(a, b) {
    if (a === b) return 1;
    var s = a.length < b.length ? a : b, l = a.length < b.length ? b : a;
    if (s.length >= 4 && l.indexOf(s) === 0 && l.length - s.length <= 3) return 0.75;
    return 0;
  }

  var VOCAB = {}; // every word that appears in some question or tag

  function scoreFaq(q, faq) {
    if (!q.length) return 0;
    var best = 0, bestEff = 1;
    for (var v = 0; v < faq._qt.length; v++) {
      var vt = faq._qt[v];
      if (!vt.length) continue;
      var vHit = 0, qHit = 0, miss = 0, i, j, m;
      for (i = 0; i < vt.length; i++) {
        for (m = 0, j = 0; j < q.length; j++) m = Math.max(m, tokMatch(vt[i], q[j]));
        vHit += m;
      }
      for (j = 0; j < q.length; j++) {
        for (m = 0, i = 0; i < vt.length; i++) m = Math.max(m, tokMatch(vt[i], q[j]));
        qHit += m;
        if (!m) miss += VOCAB[q[j]] ? 0.5 : 1; // unknown words count against the match more
      }
      if (!vHit || !qHit) continue;
      var recall = vHit / vt.length, cover = qHit / q.length;
      var eff = 1 - miss / q.length;
      var s = (2 * recall * cover / (recall + cover)) * eff;
      if (s > best) { best = s; bestEff = eff; }
    }
    var bonus = 0;
    for (var k = 0; k < faq._tags.length; k++) if (q.indexOf(faq._tags[k]) !== -1) bonus += 0.15;
    return best + Math.min(bonus, 0.3) * bestEff;
  }

  function prepare(list) {
    var showSamples = (window.Shopify && window.Shopify.designMode) || /[?&]a1chat_samples=1/.test(location.search);
    var out = (Array.isArray(list) ? list : []).filter(function (f) {
      return f && f.id && f.answer && f.questions && f.questions.length && (!f.sample || showSamples);
    }).map(function (f) {
      f._qt = f.questions.map(tokens);
      f._tags = [];
      (f.tags || []).forEach(function (t) { t = tokens(t)[0] || t; if (f._tags.indexOf(t) === -1) f._tags.push(t); });
      return f;
    });
    VOCAB = {};
    out.forEach(function (f) {
      f._qt.forEach(function (vt) { vt.forEach(function (t) { VOCAB[t] = 1; }); });
      f._tags.forEach(function (t) { VOCAB[t] = 1; });
    });
    return out;
  }

  /* ---------- business hours (Eastern) + federal holiday closures ----------
     Mon-Thu 9AM-5PM, Fri 8:30AM-3PM. Closed weekends and the 11 federal holidays; a holiday on a
     Saturday is observed Friday, on a Sunday the following Monday. One-off closures go on the Contact page. */
  var WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var DAYNAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  function etParts(d) {
    try {
      var o = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short'
      }).formatToParts(d).forEach(function (p) { o[p.type] = p.value; });
      return { y: +o.year, m: +o.month, d: +o.day, h: (+o.hour) % 24, min: +o.minute, wd: WD[o.weekday] };
    } catch (e) {
      return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), h: d.getHours(), min: d.getMinutes(), wd: d.getDay() };
    }
  }
  function utc(y, m, d) { return Date.UTC(y, m - 1, d); }
  function nthWeekday(y, m, wd, n) { var first = new Date(utc(y, m, 1)).getUTCDay(); return 1 + ((wd - first + 7) % 7) + (n - 1) * 7; }
  function lastWeekday(y, m, wd) { var last = new Date(Date.UTC(y, m, 0)); return last.getUTCDate() - ((last.getUTCDay() - wd + 7) % 7); }
  function observed(y, m, d) {
    var t = utc(y, m, d), wd = new Date(t).getUTCDay();
    return wd === 6 ? t - DAY : wd === 0 ? t + DAY : t;
  }
  function federalHolidays(y) {
    return [
      [observed(y, 1, 1), "New Year's Day"],
      [utc(y, 1, nthWeekday(y, 1, 1, 3)), 'Martin Luther King Jr. Day'],
      [utc(y, 2, nthWeekday(y, 2, 1, 3)), "Presidents' Day"],
      [utc(y, 5, lastWeekday(y, 5, 1)), 'Memorial Day'],
      [observed(y, 6, 19), 'Juneteenth'],
      [observed(y, 7, 4), 'Independence Day'],
      [utc(y, 9, nthWeekday(y, 9, 1, 1)), 'Labor Day'],
      [utc(y, 10, nthWeekday(y, 10, 1, 2)), 'Columbus Day'],
      [observed(y, 11, 11), 'Veterans Day'],
      [utc(y, 11, nthWeekday(y, 11, 4, 4)), 'Thanksgiving'],
      [observed(y, 12, 25), 'Christmas Day']
    ];
  }
  function closedFor(t) { // t = UTC midnight of an Eastern calendar day
    var y = new Date(t).getUTCFullYear();
    var list = federalHolidays(y).concat(federalHolidays(y + 1)); // Jan 1 on a Saturday is observed Dec 31
    for (var i = 0; i < list.length; i++) if (list[i][0] === t) return list[i][1];
    return null;
  }
  function hoursFor(wd) { return wd === 0 || wd === 6 ? null : wd === 5 ? [510, 900] : [540, 1020]; }
  function isOpenNow() {
    var p = etParts(now()), t = utc(p.y, p.m, p.d), h = hoursFor(p.wd), mins = p.h * 60 + p.min;
    return !!h && !closedFor(t) && mins >= h[0] && mins < h[1];
  }
  function fmtTime(mins) {
    var h = Math.floor(mins / 60), m = mins % 60;
    return (h > 12 ? h - 12 : h) + (m ? ':' + (m < 10 ? '0' : '') + m : '') + (h >= 12 ? 'PM' : 'AM');
  }
  function closedNoteHtml() {
    var p = etParts(now()), today = utc(p.y, p.m, p.d), mins = p.h * 60 + p.min;
    var holiday = closedFor(today), when = '';
    for (var i = 0; i < 14 && !when; i++) {
      var t = today + i * DAY, wd = new Date(t).getUTCDay(), h = hoursFor(wd);
      if (!h || closedFor(t)) continue;
      if (i === 0) { if (mins < h[0]) when = 'today at ' + fmtTime(h[0]); continue; }
      when = (i === 1 ? 'tomorrow' : DAYNAME[wd]) + ' at ' + fmtTime(h[0]);
    }
    var lead = holiday ? 'Heads up — we’re closed today for ' + holiday + '.' : 'Heads up — we’re closed right now.';
    return '<div class="a1-chat-hours-note">' + esc(lead) + (when ? ' We ' + (holiday ? 'reopen ' : 'open ') + esc(when) + '.' : '') +
      ' <a href="' + HOURS_URL + '">Hours &amp; contact</a></div>';
  }

  /* ---------- holiday costumes ----------
     Each look: a hat drawn around the brim centre (0,0) in pose-image pixels, an optional cape behind
     him, a decoration that stays at his parked spot, and a light ambient effect. Hats/capes ride on the
     figure, so they move, bob and mirror with him. */
  function heart(x, y, s, fill) {
    return '<path transform="translate(' + x + ',' + y + ') scale(' + s + ')" fill="' + fill + '" d="M0,4 C-8,-2 -7,-9 -3.5,-9 C-1.5,-9 0,-7.5 0,-6 C0,-7.5 1.5,-9 3.5,-9 C7,-9 8,-2 0,4 Z"/>';
  }
  function shamrock(x, y, s) {
    return '<g transform="translate(' + x + ',' + y + ') scale(' + s + ')" fill="#2f9e4f">' +
      '<circle cx="0" cy="-3.4" r="3.1"/><circle cx="-3.1" cy="1.4" r="3.1"/><circle cx="3.1" cy="1.4" r="3.1"/>' +
      '<path d="M0,1 Q1,5 3,7.5" stroke="#2f9e4f" stroke-width="1.3" fill="none"/></g>';
  }
  function star(x, y, r, fill) {
    var pts = [];
    for (var i = 0; i < 10; i++) {
      var a = Math.PI / 5 * i - Math.PI / 2, rr = i % 2 ? r * 0.45 : r;
      pts.push((x + rr * Math.cos(a)).toFixed(1) + ',' + (y + rr * Math.sin(a)).toFixed(1));
    }
    return '<polygon fill="' + fill + '" points="' + pts.join(' ') + '"/>';
  }
  var HATS = {
    christmas:
      '<path d="M-22,-4 C-22,-24 -12,-38 2,-41 C14,-43 22,-30 23,-4 Z" fill="#d42a2a"/>' +
      '<path d="M1,-41 C-12,-47 -26,-41 -31,-26 L-25,-24 C-21,-33 -12,-37 -3,-34 Z" fill="#b51f1f"/>' +
      '<path d="M-10,-29 C-4,-36 5,-38 11,-35" stroke="#f06b6b" stroke-width="2" fill="none"/>' +
      '<rect x="-26" y="-10" width="52" height="12" rx="6" fill="#fff"/>' +
      '<path d="M-22,-1 Q0,3 22,-1" stroke="#dde7f1" stroke-width="2" fill="none"/>' +
      '<circle cx="-29" cy="-23" r="6.5" fill="#fff"/><circle cx="-30.5" cy="-24.5" r="2.5" fill="#f2f6fa"/>',
    halloween:
      '<ellipse cx="0" cy="-2" rx="34" ry="7" fill="#24143a"/><ellipse cx="0" cy="-3.6" rx="29" ry="4.4" fill="#3b2259"/>' +
      '<path d="M-17,-5 C-13,-22 -9,-36 -5,-46 C-8,-53 -15,-58 -25,-58 C-15,-53 -6,-47 1,-41 C7,-28 13,-15 17,-5 Z" fill="#3b2259"/>' +
      '<path d="M-3,-44 C0,-36 4,-24 8,-10" stroke="#55357d" stroke-width="2" fill="none"/>' +
      '<path d="M-15.5,-12.5 L15.5,-12.5 L17,-5.5 L-17,-5.5 Z" fill="#f28c1c"/>' +
      '<rect x="-4" y="-13" width="8" height="7.5" rx="1" fill="none" stroke="#ffd34d" stroke-width="1.7"/>',
    valentines:
      '<path d="M-23,-1 Q0,-31 23,-1" stroke="#e8457a" stroke-width="3.6" fill="none" stroke-linecap="round"/>' +
      '<path d="M-9,-13 l-2,-5 l4,-3 l-4,-3 l4,-3 l-4,-3 l1,-5" stroke="#8f99a5" stroke-width="1.6" fill="none"/>' +
      '<path d="M9,-13 l2,-5 l-4,-3 l4,-3 l-4,-3 l4,-3 l-1,-5" stroke="#8f99a5" stroke-width="1.6" fill="none"/>' +
      heart(-10, -40, 1.25, '#e8457a') + heart(10, -40, 1.25, '#ff6f9a'),
    dental:
      '<path d="M-23,-3 Q0,-13 23,-3" stroke="#56626f" stroke-width="4.5" fill="none" stroke-linecap="round"/>' +
      '<circle cx="13" cy="-13" r="9.5" fill="#e3ecf4" stroke="#7d8a97" stroke-width="2"/>' +
      '<circle cx="13" cy="-13" r="2.6" fill="#33404d"/><path d="M7.5,-17 Q10.5,-20.5 15,-19.5" stroke="#fff" stroke-width="1.8" fill="none"/>',
    stpatricks:
      '<ellipse cx="0" cy="-2" rx="27" ry="5.6" fill="#1b6431"/>' +
      '<path d="M-15,-4 L-17,-36 Q0,-39 17,-36 L15,-4 Z" fill="#23853f"/><ellipse cx="0" cy="-36" rx="17" ry="3.5" fill="#2e9b4d"/>' +
      '<path d="M-15.3,-13 L15.3,-13 L15.6,-5.5 L-15.6,-5.5 Z" fill="#1c1c1c"/>' +
      '<rect x="-4.5" y="-13.5" width="9" height="8.5" rx="1" fill="none" stroke="#f5c542" stroke-width="2"/>' +
      shamrock(11, -24, 1.15),
    july4:
      '<ellipse cx="0" cy="-2" rx="27" ry="5.6" fill="#1f3d8a"/>' +
      '<path d="M-15,-4 L-17,-36 Q0,-39 17,-36 L15,-4 Z" fill="#fff"/>' +
      '<rect x="-16.6" y="-33" width="33.2" height="4" fill="#d22f3a"/><rect x="-16.2" y="-25" width="32.4" height="4" fill="#d22f3a"/>' +
      '<rect x="-15.8" y="-17.5" width="31.6" height="3.6" fill="#d22f3a"/>' +
      '<ellipse cx="0" cy="-36" rx="17" ry="3.5" fill="#f1f4f8"/>' +
      '<path d="M-15.4,-13 L15.4,-13 L15.7,-5 L-15.7,-5 Z" fill="#1f3d8a"/>' +
      star(-8, -9, 2.6, '#fff') + star(0, -9, 2.6, '#fff') + star(8, -9, 2.6, '#fff'),
    thanksgiving:
      '<ellipse cx="0" cy="-2" rx="28" ry="5.6" fill="#1e1e1e"/>' +
      '<path d="M-14,-4 L-12,-34 L12,-34 L14,-4 Z" fill="#3a3a3a"/><ellipse cx="0" cy="-34" rx="12" ry="2.6" fill="#4a4a4a"/>' +
      '<path d="M-13.6,-13 L13.6,-13 L14,-5 L-14,-5 Z" fill="#151515"/>' +
      '<rect x="-5" y="-13.6" width="10" height="9.2" rx="1" fill="none" stroke="#e2b13c" stroke-width="2.2"/>',
    newyear:
      '<g transform="translate(0,-4) rotate(12)">' +
      '<path d="M-15,0 L0,-40 L15,0 Z" fill="#7b3fe4"/>' +
      '<circle cx="-5" cy="-11" r="2.2" fill="#f5c542"/><circle cx="5" cy="-6" r="2.2" fill="#f5c542"/>' +
      '<circle cx="2" cy="-19" r="2" fill="#f5c542"/><circle cx="-2" cy="-28" r="1.7" fill="#f5c542"/>' +
      '<path d="M-15,0 Q0,4 15,0 L14,-3.5 Q0,0.5 -14,-3.5 Z" fill="#f5c542"/>' +
      '<circle cx="0" cy="-41" r="4.6" fill="#f5c542"/></g>'
  };
  var HAT_SCALE = { christmas: 1.15, halloween: 1.25, valentines: 1.2, dental: 1.05, stpatricks: 1.3, july4: 1.3, thanksgiving: 1.3, newyear: 1.3 };
  var CAPE =
    '<path d="M41,100 C33,140 22,196 12,240 Q21,233 30,242 Q40,233 50,243 Q61,233 72,243 Q83,233 94,242 Q103,233 112,240 C102,196 91,140 83,100 Z" fill="#3b2259"/>' +
    '<path d="M41,100 C35,130 28,170 22,205" stroke="#4d2f72" stroke-width="3" fill="none"/>' +
    '<path d="M41,104 L23,81 Q34,87 48,97 Z" fill="#f28c1c"/><path d="M83,104 L101,81 Q90,87 76,97 Z" fill="#f28c1c"/>';
  // brim centre (x, y) and lean (deg) of each pose, in that pose's image pixels
  var POSE_GEO = {
    idle: { w: 132, x: 62, y: 38, r: 0, cape: '' },
    point: { w: 151, x: 72, y: 38, r: 4, cape: 'translate(4,0) rotate(3 62 250)' },
    scurry: { w: 197, x: 124, y: 37, r: 13, cape: 'translate(22,0) rotate(13 62 252)' }
  };
  // decorations at his parked spot (each sized as a % of the mascot box width)
  var HOME = {
    christmas:
      '<svg class="a1h-item a1h-gift" viewBox="0 0 30 28"><rect x="2" y="9" width="26" height="19" rx="2" fill="#2f8f4e"/>' +
      '<rect x="1" y="6" width="28" height="6" rx="1.5" fill="#37a35a"/><rect x="13" y="6" width="4" height="22" fill="#f5c542"/>' +
      '<path d="M15,6 C10,0 5,2 8,5 C10,7 13,6 15,6 C17,6 20,7 22,5 C25,2 20,0 15,6 Z" fill="#f5c542"/></svg>' +
      '<svg class="a1h-item a1h-snow" viewBox="0 0 120 22"><path d="M2,22 C5,13 17,9 28,10 C36,4 50,3 60,7 C70,2 86,4 94,10 C104,9 116,13 118,22 Z" fill="#fff"/>' +
      '<path d="M10,22 C22,17 42,16 60,18 C80,16 100,17 112,22 Z" fill="#dce8f4"/>' +
      '<circle cx="40" cy="9" r="1.2" fill="#cfe3ff"/><circle cx="78" cy="7" r="1.2" fill="#cfe3ff"/></svg>',
    halloween:
      '<svg class="a1h-item a1h-pumpkin" viewBox="0 0 40 34"><ellipse cx="20" cy="21" rx="18" ry="12.5" fill="#e0701a"/>' +
      '<ellipse cx="12" cy="21" rx="8" ry="12" fill="#ee8526"/><ellipse cx="28" cy="21" rx="8" ry="12" fill="#ee8526"/>' +
      '<ellipse cx="20" cy="21" rx="6.5" ry="12.5" fill="#f39a3d"/>' +
      '<path d="M18.5,9.5 Q18,4 22,1.5 L23.6,3.2 Q21.2,5.5 21.4,9.5 Z" fill="#4c7a2a"/>' +
      '<g class="a1h-glow" fill="#ffd34d"><path d="M10,18 L13.5,12.5 L17,18 Z"/><path d="M23,18 L26.5,12.5 L30,18 Z"/>' +
      '<path d="M18.6,21 L20,18.4 L21.4,21 Z"/>' +
      '<path d="M8.5,23.5 Q20,32 31.5,23.5 L28.6,24.8 L27,27.3 L24.6,25.4 L22,28.2 L19.6,25.8 L17,28 L14.6,25.4 L12.8,26.8 Z"/></g></svg>',
    valentines:
      '<svg class="a1h-item a1h-balloon" viewBox="0 0 30 80"><path d="M15,30 C16,42 12,52 15,62 C17,70 13,75 15,80" stroke="#9aa6b2" stroke-width="1" fill="none"/>' +
      heart(15, 18, 2.6, '#e8457a') + '<path d="M8,6 Q10,3 13,4" stroke="#ffb3c9" stroke-width="1.6" fill="none"/>' +
      '<path d="M13.5,29 L16.5,29 L15,31.5 Z" fill="#c8315f"/></svg>',
    dental:
      '<svg class="a1h-item a1h-tooth" viewBox="0 0 34 38"><path d="M7,4 C3,4 1,9 2,15 C3,22 6,26 7,33 C8,37 12,37 13,33 L15,26 C16,24 18,24 19,26 L21,33 C22,37 26,37 27,33 C28,26 31,22 32,15 C33,9 31,4 27,4 C23,4 21,6 17,6 C13,6 11,4 7,4 Z" fill="#fff" stroke="#9cc4e4" stroke-width="1.4"/>' +
      '<circle cx="12" cy="14.5" r="1.9" fill="#263445"/><circle cx="22" cy="14.5" r="1.9" fill="#263445"/>' +
      '<path d="M13,19.5 Q17,23.5 21,19.5" stroke="#263445" stroke-width="1.4" fill="none" stroke-linecap="round"/>' +
      '<circle cx="8.5" cy="19" r="2" fill="#ffc2cf"/><circle cx="25.5" cy="19" r="2" fill="#ffc2cf"/>' +
      '<path d="M7,7 Q9,5 12,6" stroke="#e6f1fb" stroke-width="1.5" fill="none"/></svg>',
    stpatricks:
      '<svg class="a1h-item a1h-pot" viewBox="0 0 40 34"><circle cx="12" cy="10" r="5" fill="#f5c542"/><circle cx="20" cy="7" r="5" fill="#ffd75e"/>' +
      '<circle cx="28" cy="10" r="5" fill="#f5c542"/><circle cx="16" cy="12" r="4" fill="#e9b52f"/><circle cx="25" cy="12" r="4" fill="#ffd75e"/>' +
      '<path d="M5,14 L35,14 C37,24 33,32 20,32 C7,32 3,24 5,14 Z" fill="#2a2a2a"/><rect x="3" y="12" width="34" height="5" rx="2.5" fill="#3a3a3a"/>' +
      '<path d="M9,20 Q11,27 17,29" stroke="#4a4a4a" stroke-width="1.6" fill="none"/></svg>',
    july4:
      '<svg class="a1h-item a1h-flag" viewBox="0 0 30 50"><path d="M3.5,2 L3.5,50" stroke="#8a6a44" stroke-width="2" stroke-linecap="round"/>' +
      '<g class="a1h-wave"><rect x="4.5" y="3" width="24" height="16" fill="#fff"/>' +
      '<rect x="4.5" y="3" width="24" height="2.3" fill="#d22f3a"/><rect x="4.5" y="7.6" width="24" height="2.3" fill="#d22f3a"/>' +
      '<rect x="4.5" y="12.2" width="24" height="2.3" fill="#d22f3a"/><rect x="4.5" y="16.7" width="24" height="2.3" fill="#d22f3a"/>' +
      '<rect x="4.5" y="3" width="10.5" height="9" fill="#1f3d8a"/>' +
      '<circle cx="7.5" cy="5.5" r=".8" fill="#fff"/><circle cx="12" cy="5.5" r=".8" fill="#fff"/><circle cx="9.7" cy="7.6" r=".8" fill="#fff"/>' +
      '<circle cx="7.5" cy="9.7" r=".8" fill="#fff"/><circle cx="12" cy="9.7" r=".8" fill="#fff"/></g></svg>',
    thanksgiving:
      '<svg class="a1h-item a1h-harvest" viewBox="0 0 46 30">' +
      '<path d="M2,29 C3,25 8,23 11,25 C9,27 6,29 2,29 Z" fill="#c4531c"/><path d="M36,29 C38,24 44,24 45,27 C42,29 39,29 36,29 Z" fill="#d8962a"/>' +
      '<ellipse cx="23" cy="19" rx="14" ry="10" fill="#e07a1f"/><ellipse cx="16" cy="19" rx="6.5" ry="9.6" fill="#ec8c2e"/>' +
      '<ellipse cx="30" cy="19" rx="6.5" ry="9.6" fill="#ec8c2e"/><ellipse cx="23" cy="19" rx="5" ry="10" fill="#f29c40"/>' +
      '<path d="M22,9.5 Q21.5,5 24.5,3 L26,4.6 Q24,6 24.4,9.5 Z" fill="#5a7d2a"/></svg>',
    newyear: ''
  };
  var FX = {
    christmas: ['snow', 9], halloween: ['bat', 2], valentines: ['heart', 6], dental: ['sparkle', 6],
    stpatricks: ['clover', 5], july4: ['firework', 3], thanksgiving: ['leaf', 6], newyear: ['confetti', 12]
  };
  var HOLIDAY_TEXT = {
    christmas: ['Merry Christmas from A1! 🎄', 'Merry Christmas! Questions? Ask me!'],
    halloween: ['Happy Halloween! 🎃', 'Boo! Questions? Ask me!'],
    valentines: ['Happy Valentine’s Day! 💘', 'Questions? I ❤️ helping!'],
    dental: ['This week we’re celebrating dentists and dental assistants — thanks for all you do! 🦷', 'Thanks for all you do! Questions?'],
    stpatricks: ['Happy St. Patrick’s Day! ☘️', 'Feeling lucky? Ask me!'],
    july4: ['Happy 4th of July! 🎆', 'Happy 4th! Questions? Ask me!'],
    thanksgiving: ['Happy Thanksgiving — we’re thankful for you! 🦃', 'Thankful for you! Questions?'],
    newyear: ['Happy New Year! 🎉', 'Happy New Year! Questions?']
  };
  function holidayFor(d) {
    var p = etParts(d), md = p.m * 100 + p.d;
    var tg = nthWeekday(p.y, 11, 4, 4); // Thanksgiving: the Sunday before through Thursday
    var W = [
      ['newyear', 1227, 1231], ['newyear', 101, 102], ['valentines', 208, 214], ['dental', 301, 307],
      ['stpatricks', 311, 317], ['july4', 628, 704], ['halloween', 1025, 1031],
      ['thanksgiving', 1100 + tg - 4, 1100 + tg], ['christmas', 1201, 1226]
    ];
    for (var i = 0; i < W.length; i++) if (md >= W[i][1] && md <= W[i][2]) return W[i][0];
    return null;
  }
  function pickHoliday(root) {
    var m = /[?&]a1chat_holiday=([a-z0-9]+)/.exec(location.search);
    if (m) return HATS[m[1]] ? m[1] : null; // preview (any page, any date); =off for none
    if (root.getAttribute('data-holidays') === 'off') return null;
    return holidayFor(now());
  }
  function costumeSvg(key, pose, layer) {
    var g = POSE_GEO[pose];
    var inner = layer === 'back'
      ? (key === 'halloween' ? '<g' + (g.cape ? ' transform="' + g.cape + '"' : '') + '>' + CAPE + '</g>' : '')
      : '<g transform="translate(' + g.x + ',' + g.y + ') rotate(' + g.r + ') scale(' + (HAT_SCALE[key] || 1) + ')">' + HATS[key] + '</g>';
    if (!inner) return '';
    return '<svg class="a1c-' + pose + '" viewBox="0 0 ' + g.w + ' 264" preserveAspectRatio="none" overflow="visible" focusable="false">' + inner + '</svg>';
  }
  function fxHtml(key) {
    var f = FX[key]; if (!f) return '';
    var colors = ['#f5c542', '#e8457a', '#5aa9e6', '#7b3fe4', '#37a35a', '#ff8a3d'];
    var leafC = ['#c4531c', '#d8962a', '#a8431a', '#e0a33a'];
    var h = '';
    for (var i = 0; i < f[1]; i++) {
      var x = Math.round(4 + (i + Math.random() * 0.8) * (92 / f[1]));
      var style = '--x:' + x + '%;--d:' + (Math.random() * 6).toFixed(2) + 's;--t:' + (4 + Math.random() * 3).toFixed(2) + 's;' +
        '--s:' + (0.7 + Math.random() * 0.6).toFixed(2) + ';--y:' + Math.round(10 + Math.random() * 45) + '%;';
      var body = '';
      if (f[0] === 'confetti') style += 'background:' + colors[i % colors.length] + ';';
      if (f[0] === 'leaf') body = '<svg viewBox="0 0 12 12"><path d="M6,0 C10,2 12,6 6,12 C0,6 2,2 6,0 Z" fill="' + leafC[i % 4] + '"/><path d="M6,2 L6,12" stroke="#7a3410" stroke-width=".6"/></svg>';
      if (f[0] === 'heart') body = '<svg viewBox="-9 -10 18 15">' + heart(0, 0, 1, i % 2 ? '#ff6f9a' : '#e8457a') + '</svg>';
      if (f[0] === 'clover') body = '<svg viewBox="-7 -7 14 15">' + shamrock(0, 0, 1) + '</svg>';
      if (f[0] === 'sparkle') body = '<svg viewBox="-6 -6 12 12"><path d="M0,-6 L1.3,-1.3 L6,0 L1.3,1.3 L0,6 L-1.3,1.3 L-6,0 L-1.3,-1.3 Z" fill="' + (i % 2 ? '#7fe0d0' : '#fff') + '"/></svg>';
      if (f[0] === 'bat') body = '<svg viewBox="0 0 30 14"><path d="M15,5 C13,2 9,1 0,3 C4,5 5,8 6,11 C8,9 11,9 13,11 C14,9 16,9 17,11 C19,9 22,9 24,11 C25,8 26,5 30,3 C21,1 17,2 15,5 Z" fill="#1d1430"/><circle cx="13.6" cy="6" r=".8" fill="#ffd34d"/><circle cx="16.4" cy="6" r=".8" fill="#ffd34d"/></svg>';
      if (f[0] === 'firework') {
        for (var r = 0; r < 8; r++) body += '<i style="--a:' + (r * 45) + 'deg"></i>';
        style += '--c:' + ['#d22f3a', '#5aa9e6', '#f5c542'][i % 3] + ';';
      }
      h += '<span class="a1fx a1fx-' + f[0] + '" style="' + style + '">' + body + '</span>';
    }
    return '<div class="a1-chat-fx" aria-hidden="true">' + h + '</div>';
  }

  /* ---------- widget ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function init() {
    var root = document.getElementById('a1Chat');
    if (!root || root.getAttribute('data-ready')) return;
    root.setAttribute('data-ready', '1');
    if (ss(DISMISS_KEY)) { root.style.display = 'none'; return; }

    var $ = function (id) { return document.getElementById(id); };
    var POSES;
    try { POSES = window.A1_CHAT_POSES || JSON.parse($('a1ChatPoses').textContent); } catch (e) { POSES = {}; }
    var PLAYFUL = root.getAttribute('data-temperament') === 'playful';
    var faqUrl = root.getAttribute('data-faq-url');

    var mascot = $('a1ChatMascot');
    var fig = $('a1ChatFig');
    var mascotImg = $('a1ChatMascotImg');
    var launcher = $('a1ChatLauncher');
    var dismissBtn = $('a1ChatDismiss');
    var bubble = $('a1ChatBubble');
    var panel = $('a1ChatPanel');
    var msgs = $('a1ChatMsgs');
    var chipsBox = $('a1ChatChips');
    var form = $('a1ChatForm');
    var input = $('a1ChatInput');
    var closeBtn = $('a1ChatClose');

    var FAQ = [];
    var state = 'parked'; // parked | roaming | pointing
    var panelOpen = false;
    var roamTimer = null, bubbleTimer = null, waveTimer = null, seq = 0, pointSeq = 0;
    var greeted = false;
    var pos = { x: 0, y: 0 }; // current translate offset from the parked spot
    var HOLIDAY = null;

    // preload poses so the first swap doesn't flash
    Object.keys(POSES).forEach(function (k) { var i = new Image(); i.src = POSES[k]; });

    function dressUp() {
      HOLIDAY = pickHoliday(root);
      if (!HOLIDAY) return;
      var front = $('a1ChatCostume'), back = $('a1ChatCostumeBack'), home = $('a1ChatHome');
      if (!front || !back) { HOLIDAY = null; return; }
      var f = '', b = '';
      ['idle', 'point', 'scurry'].forEach(function (p) { f += costumeSvg(HOLIDAY, p, 'front'); b += costumeSvg(HOLIDAY, p, 'back'); });
      front.innerHTML = f;
      back.innerHTML = b;
      if (home) home.innerHTML = HOME[HOLIDAY] + fxHtml(HOLIDAY);
      root.setAttribute('data-holiday', HOLIDAY);
      root.classList.add('a1chat-costumed');
    }

    function setPose(name, faceLeft) {
      var src = POSES[name] || POSES.idle;
      if (src && mascotImg.getAttribute('src') !== src) mascotImg.src = src;
      fig.style.scale = faceLeft ? '-1 1' : '';
      mascot.setAttribute('data-pose', name);
    }

    function parkedRect() {
      // the parked spot is wherever CSS puts the mascot with no translate applied
      var r = mascot.getBoundingClientRect();
      return { x: r.left - pos.x, y: r.top - pos.y, w: r.width, h: r.height };
    }

    function travel(x, y, then) {
      var p = parkedRect();
      var dx = x - p.x, dy = y - p.y;
      var faceLeft = dx < pos.x; // face the direction of travel
      mascot.classList.add('a1chat-moving');
      setPose('scurry', faceLeft);
      pos = { x: dx, y: dy };
      mascot.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      var my = ++seq;
      setTimeout(function () { if (my === seq && then) then(); }, REDUCED ? 0 : TRAVEL_MS);
    }

    function goHome() {
      hideBubble();
      var p = parkedRect();
      travel(p.x, p.y, function () {
        mascot.classList.remove('a1chat-moving');
        mascot.style.transform = '';
        pos = { x: 0, y: 0 };
        setPose('idle');
        state = 'parked';
        scheduleRoam();
      });
    }

    function placeBubble() {
      var r = mascot.getBoundingClientRect();
      bubble.classList.toggle('a1chat-below', r.top < 90);
      var cx = r.left + r.width / 2;
      bubble.classList.toggle('a1chat-align-right', cx > window.innerWidth - 140);
      bubble.classList.toggle('a1chat-align-left', cx < 140);
    }

    function showBubble(text, ms) {
      bubble.textContent = text;
      placeBubble();
      bubble.classList.add('a1chat-show');
      clearTimeout(bubbleTimer);
      bubbleTimer = setTimeout(hideBubble, ms || 3200);
    }
    function hideBubble() { bubble.classList.remove('a1chat-show'); }

    function scheduleRoam(ms) {
      clearTimeout(roamTimer);
      if (!PLAYFUL || REDUCED || isMobile() || panelOpen || state !== 'parked') return;
      roamTimer = setTimeout(roam, ms || ROAM_DELAY);
    }

    function scheduleWave() {
      clearTimeout(waveTimer);
      if (REDUCED) return;
      waveTimer = setTimeout(function () {
        if (state === 'parked' && !panelOpen && document.visibilityState !== 'hidden') {
          mascot.classList.add('a1chat-wave');
          setTimeout(function () { mascot.classList.remove('a1chat-wave'); }, 1600);
        }
        scheduleWave();
      }, WAVE_EVERY + Math.random() * 15000);
    }

    function roam() {
      if (!PLAYFUL || panelOpen || state !== 'parked' || REDUCED || isMobile()) return;
      state = 'roaming';
      hideBubble();
      var p = parkedRect();
      // bottom lane only: same y as the parked spot, anywhere across the width
      var destX = 16 + Math.random() * Math.max(40, window.innerWidth - p.w - 32);
      travel(destX, p.y, function () {
        mascot.classList.remove('a1chat-moving');
        setPose('idle');
        showBubble('Psst — questions? Ask me!', 2600);
        setTimeout(function () { if (state === 'roaming') goHome(); }, 2800);
      });
    }

    function findTarget(key) {
      var els = document.querySelectorAll('[data-chat-target="' + key + '"]');
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && getComputedStyle(els[i]).visibility !== 'hidden') return els[i];
      }
      return null;
    }

    function canPoint() { return !REDUCED && !isMobile(); }

    // Pretend click: glow + ripple on the element. Never calls el.click().
    function pressTarget(el, cx, cy) {
      el.classList.remove('a1chat-press');
      void el.offsetWidth; // restart the animation if it's already running
      el.classList.add('a1chat-glow', 'a1chat-press');
      clearTimeout(el._a1chatGlow);
      el._a1chatGlow = setTimeout(function () { el.classList.remove('a1chat-glow', 'a1chat-press'); }, GLOW_MS);
      if (cx != null && !REDUCED) {
        var rp = document.createElement('span');
        rp.className = 'a1-chat-ripple';
        rp.style.left = cx + 'px'; rp.style.top = cy + 'px';
        root.appendChild(rp);
        setTimeout(function () { rp.remove(); }, 700);
      }
    }

    function inView(el) {
      var r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    }

    // Calls back once the element has stopped moving (smooth scrolling can run well past a second).
    function whenSettled(el, cb) {
      var last = null, still = 0, t0 = Date.now();
      (function check() {
        var r = el.getBoundingClientRect();
        if (last && Math.abs(r.top - last.top) < 0.5 && Math.abs(r.left - last.left) < 0.5) still++;
        else still = 0;
        last = r;
        if (still >= 5 || Date.now() - t0 > 2500) cb(); else setTimeout(check, 40);
      })();
    }

    // Where he should stand so his fingertip lands on the middle of el.
    function spotFor(el) {
      var r = el.getBoundingClientRect();
      var p = parkedRect();
      var w = p.w, h = p.h, gap = 4;
      var faceLeft = false, x;
      if (r.left - w - gap >= 8) { x = r.left - w - gap; } // stand left of it, point right
      else { x = r.right + gap; faceLeft = true; } // otherwise stand right, point left
      x = Math.max(8, Math.min(x, window.innerWidth - w - 8));
      var y = Math.max(8, Math.min(r.top + r.height / 2 - h * HAND_Y, window.innerHeight - h - 8));
      return { x: x, y: y, faceLeft: faceLeft, px: p };
    }

    // Returns true if he is (or will be) pointing at the target on this page.
    function pointAt(targetKey, note) {
      if (!targetKey) return false;
      var el = findTarget(targetKey);
      if (!el) return false;
      if (!canPoint()) {
        if (REDUCED && !isMobile() && inView(el)) pressTarget(el);
        return false;
      }
      clearTimeout(roamTimer);
      hideBubble();
      state = 'pointing';
      var mine = ++pointSeq;
      var r0 = el.getBoundingClientRect();
      if (r0.top < 0 || r0.bottom > window.innerHeight - 20) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
      }
      whenSettled(el, function () {
        if (mine !== pointSeq) return;
        var s1 = spotFor(el);
        travel(s1.x, s1.y, function () {
          // the page may have shifted while he walked (late images, a sticky header): fine-tune his aim
          var s2 = spotFor(el);
          var fix = Math.abs(s2.x - s1.x) > 2 || Math.abs(s2.y - s1.y) > 2;
          if (fix) {
            mascot.style.transition = REDUCED ? 'none' : 'transform 0.25s ease-out';
            pos = { x: s2.x - s2.px.x, y: s2.y - s2.px.y };
            mascot.style.transform = 'translate(' + pos.x + 'px,' + pos.y + 'px)';
          }
          setTimeout(function () {
            if (mine !== pointSeq) return;
            mascot.style.transition = '';
            arrive(el, s2.faceLeft, note, mine);
          }, fix ? 260 : 0);
        });
      });
      return true;
    }

    function arrive(el, faceLeft, note, mine) {
      mascot.classList.remove('a1chat-moving');
      setPose('point', faceLeft);
      if (note) showBubble(note, POINT_HOLD + 300);
      var sy = window.scrollY;
      var onScroll = function () { // visitor scrolled away mid-point: stop pointing at empty air
        if (Math.abs(window.scrollY - sy) > 40 && state === 'pointing' && mine === pointSeq) { window.removeEventListener('scroll', onScroll); goHome(); }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(function () {
        if (state !== 'pointing' || mine !== pointSeq) return;
        mascot.style.setProperty('--a1-dir', faceLeft ? -1 : 1);
        mascot.classList.add('a1chat-tap');
        setTimeout(function () { // fingertip meets the element
          var rr = el.getBoundingClientRect();
          pressTarget(el, faceLeft ? rr.right - 6 : rr.left + 6, rr.top + rr.height / 2);
        }, 170);
        setTimeout(function () { mascot.classList.remove('a1chat-tap'); }, 460);
      }, TAP_AT);
      setTimeout(function () {
        window.removeEventListener('scroll', onScroll);
        if (state === 'pointing' && mine === pointSeq) goHome();
      }, POINT_HOLD);
    }

    /* ---------- messaging ---------- */
    function addMsg(html, who) {
      var d = document.createElement('div');
      d.className = 'a1-chat-msg ' + who;
      d.innerHTML = html;
      msgs.appendChild(d);
      msgs.scrollTop = msgs.scrollHeight;
      return d;
    }

    function linksHtml(links) {
      if (!links || !links.length) return '';
      return '<div class="a1-chat-links">' + links.map(function (l) {
        return '<a href="' + esc(l.url) + '"' + (l.faq ? ' data-faq="' + esc(l.faq) + '"' : '') + '>' + esc(l.label) + '</a>';
      }).join('') + '</div>';
    }

    function thumbsHtml() {
      return '<div class="a1-chat-thumbs"><span>Helpful?</span><button type="button" data-t="up" aria-label="Helpful">👍</button>' +
        '<button type="button" data-t="down" aria-label="Not helpful">👎</button></div>';
    }

    function emit(name, detail) {
      try {
        window.dataLayer = window.dataLayer || [];
        var o = { event: name };
        for (var k in detail) if (Object.prototype.hasOwnProperty.call(detail, k)) o[k] = detail[k];
        window.dataLayer.push(o);
      } catch (e) { /* noop */ }
    }

    function bind(node, faqId) {
      node.querySelectorAll('.a1-chat-thumbs button').forEach(function (b) {
        b.addEventListener('click', function () {
          var v = b.getAttribute('data-t');
          b.parentElement.innerHTML = '<span class="a1chat-thanks">Thanks for the feedback!</span>';
          try {
            var log = JSON.parse(localStorage.getItem('a1chat_thumbs') || '[]');
            log.push({ id: faqId, v: v, t: Date.now() });
            localStorage.setItem('a1chat_thumbs', JSON.stringify(log.slice(-50)));
          } catch (e) { /* private mode */ }
          emit('chatbot_thumbs', { faq_id: faqId, value: v });
        });
      });
      node.querySelectorAll('[data-faq]').forEach(function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          var f = byId(a.getAttribute('data-faq'));
          if (f) { addMsg(esc(f.chip || f.questions[0]), 'user'); answer(f, 1); }
        });
      });
    }

    function byId(id) {
      for (var i = 0; i < FAQ.length; i++) if (FAQ[i].id === id) return FAQ[i];
      return null;
    }

    function answer(f, score) {
      var links = (f.links || []).slice();
      var pointed = false;
      if (f.point_to && f.point_to.target) {
        pointed = pointAt(f.point_to.target, f.point_to.note);
        // target isn't on this page (or he can't move): offer a link that takes them there
        var page = f.point_to.page;
        var already = links.some(function (l) { return l.url === page; });
        if (!pointed && canPoint() && page && page !== location.pathname && !already) {
          links.push({ label: 'Show me', url: page + HASH_PREFIX + encodeURIComponent(f.point_to.target) });
        }
      }
      var bodyHtml = esc(f.answer) + linksHtml(links) + thumbsHtml();
      var aboutCalling = /\bcall\b|443-552/i.test(f.answer) || (f.tags || []).indexOf('hours') !== -1;
      if (aboutCalling && !isOpenNow()) bodyHtml = closedNoteHtml() + bodyHtml;
      var node = addMsg(bodyHtml, 'bot');
      bind(node, f.id);
      emit('chatbot_question', { faq_id: f.id, score: Math.round(score * 100) / 100, pointed: pointed });
    }

    function bestMatches(q, n) {
      var qt = tokens(q);
      return FAQ.map(function (f) { return { f: f, s: scoreFaq(qt, f) }; })
        .sort(function (a, b) { return b.s - a.s; })
        .slice(0, n || 3);
    }

    function respond(userText) {
      addMsg(esc(userText), 'user');
      var typing = addMsg('<span class="a1-chat-typing" aria-label="Typing"><span></span><span></span><span></span></span>', 'bot');
      var t0 = Date.now();
      var top = bestMatches(userText, 3);
      var finish = function (fn) { setTimeout(function () { typing.remove(); fn(); }, Math.max(0, TYPING_MS - (Date.now() - t0))); };
      if (top.length && top[0].s >= THRESHOLD) finish(function () { answer(top[0].f, top[0].s); });
      else searchProducts(userText, function (products) { finish(function () { fallback(userText, top, products); }); });
    }

    /* Product search: Shopify's built-in predictive search (live catalog, published products only).
       Results only count when they really match what was typed: a part number, or most of the words. */
    var PSTOP = { handpiece: 1, handpieces: 1, dental: 1, part: 1, parts: 1, product: 1, products: 1, price: 1, cost: 1, buy: 1, order: 1, new: 1 };
    function searchProducts(query, done) {
      var words = (query.toLowerCase().replace(/[’']/g, '').match(/[a-z0-9]+(?:[-\/.][a-z0-9]+)*/g) || [])
        .filter(function (w) { return !STOP[w] && !PSTOP[w] && (w.length > 1 || /\d/.test(w)); });
      if (!words.length || !window.fetch) { done([]); return; }
      var skuLike = words.some(function (w) { return /\d/.test(w) && (/[a-z]/.test(w) || w.length >= 3); });
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = base + 'search/suggest.json?q=' + encodeURIComponent(words.join(' ')) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.sku,vendor,tag';
      var finished = false;
      var finish = function (list) { if (!finished) { finished = true; done(list); } };
      setTimeout(function () { finish([]); }, 4000);
      fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (d) {
          var list = (d && d.resources && d.resources.results && d.resources.results.products) || [];
          var content = words.filter(function (w) { return w.length >= 3 || /\d/.test(w); }).map(stem);
          var need = Math.ceil(content.length / 2);
          var out = [];
          list.forEach(function (p) {
            if (!p || !p.title || !p.url || out.length >= 4) return;
            if (!skuLike) {
              var tw = p.title.toLowerCase().replace(/[’']/g, '').split(/[^a-z0-9]+/).map(stem);
              var hits = content.filter(function (w) { return tw.some(function (t) { return tokMatch(t, w) || t === w; }); }).length;
              if (hits < need || !hits) return;
            }
            out.push({ label: p.title, url: p.url });
          });
          finish(out);
        })
        .catch(function () { finish([]); });
    }

    function fallback(userText, top, products) {
      if (products && products.length) {
        var hp = "Here's what I found in the catalog:" + linksHtml(products);
        hp += '<div class="a1-chat-sep">Or reach a real person:</div>' + linksHtml([
          { label: 'Call ' + PHONE, url: 'tel:+1' + PHONE.replace(/-/g, '') }
        ]);
        bind(addMsg(hp, 'bot'), null);
        emit('chatbot_fallback', { query: userText.slice(0, 80), product_results: products.length });
        return;
      }
      var useful = top.filter(function (t) { return t.s > 0.12; });
      var h = "I'm not sure about that one";
      if (useful.length) {
        h += ' — here are the closest topics:' + linksHtml(useful.map(function (t) {
          return { label: t.f.chip || t.f.questions[0], url: '#', faq: t.f.id };
        }));
      } else {
        h += '.';
      }
      h += '<div class="a1-chat-sep">Or reach a real person:</div>' + linksHtml([
        { label: 'Call ' + PHONE, url: 'tel:+1' + PHONE.replace(/-/g, '') },
        { label: 'Email us', url: 'mailto:' + EMAIL },
        { label: 'Repair page', url: '/pages/repairs' }
      ]);
      bind(addMsg(h, 'bot'), null);
      emit('chatbot_fallback', { query: userText.slice(0, 80) });
    }

    function renderChips() {
      chipsBox.innerHTML = '';
      FAQ.slice(0, 4).forEach(function (f) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = f.chip || f.questions[0];
        b.addEventListener('click', function () {
          chipsBox.innerHTML = '';
          addMsg(esc(f.chip || f.questions[0]), 'user');
          var typing = addMsg('<span class="a1-chat-typing"><span></span><span></span><span></span></span>', 'bot');
          setTimeout(function () { typing.remove(); answer(f, 1); }, TYPING_MS);
        });
        chipsBox.appendChild(b);
      });
    }

    function openPanel() {
      panelOpen = true;
      panel.hidden = false;
      launcher.setAttribute('aria-expanded', 'true');
      root.classList.add('a1chat-open');
      clearTimeout(roamTimer);
      hideBubble();
      if (state === 'roaming') goHome();
      if (!isMobile()) input.focus();
      if (!greeted) {
        greeted = true;
        var hi = HOLIDAY ? HOLIDAY_TEXT[HOLIDAY][0] + ' ' : '';
        addMsg(esc(hi + "Hi! I'm Spinny. Ask me about repairs, returns, parts — or pick a topic below."), 'bot');
        renderChips();
      }
      emit('chatbot_open', {});
    }

    function closePanel() {
      panelOpen = false;
      panel.hidden = true;
      launcher.setAttribute('aria-expanded', 'false');
      root.classList.remove('a1chat-open');
      scheduleRoam();
    }

    /* ---------- events ---------- */
    launcher.addEventListener('click', function () { if (panelOpen) closePanel(); else openPanel(); });
    closeBtn.addEventListener('click', function () { closePanel(); launcher.focus(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panelOpen) { closePanel(); launcher.focus(); }
    });
    dismissBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      ss(DISMISS_KEY, '1');
      clearTimeout(roamTimer); clearTimeout(bubbleTimer); seq++;
      root.style.display = 'none';
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = input.value.trim();
      if (!v) return;
      input.value = '';
      chipsBox.innerHTML = '';
      respond(v.slice(0, 300));
    });
    ['pointerdown', 'keydown', 'scroll'].forEach(function (ev) {
      window.addEventListener(ev, function () { if (state === 'parked') scheduleRoam(); }, { passive: true });
    });
    var ap = etParts(now());
    if (ap.m === 4 && ap.d === 1 && !REDUCED && !isMobile()) { // April Fools: he swats at the cursor
      var swatCool = 0;
      document.addEventListener('mousemove', function (e) {
        if (state !== 'parked' || panelOpen) return;
        var t = Date.now();
        if (t - swatCool < 4000) return;
        var r = mascot.getBoundingClientRect();
        var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        if (Math.hypot(e.clientX - cx, e.clientY - cy) < 110) {
          swatCool = t;
          mascot.style.setProperty('--a1-swat-dir', e.clientX < cx ? -1 : 1);
          fig.classList.remove('a1chat-swat');
          void fig.offsetWidth;
          fig.classList.add('a1chat-swat');
          setTimeout(function () { fig.classList.remove('a1chat-swat'); }, 600);
        }
      }, { passive: true });
    }
    window.addEventListener('resize', function () {
      if (state === 'parked') { mascot.style.transform = ''; pos = { x: 0, y: 0 }; }
    });

    function fallbackTags() {
      var have = {};
      var tagged = document.querySelectorAll('[data-chat-target]');
      for (var k = 0; k < tagged.length; k++) have[tagged[k].getAttribute('data-chat-target')] = 1;
      var hasAll = function (t, words) {
        for (var w = 0; w < words.length; w++) if (t.indexOf(words[w]) === -1) return false;
        return true;
      };
      var as = document.querySelectorAll('a, button');
      for (var i = 0; i < as.length; i++) {
        var a = as[i];
        if (a.hasAttribute('data-chat-target') || root.contains(a)) continue;
        var t = (' ' + (a.textContent || '').toLowerCase().replace(/\s+/g, ' ') + ' ');
        var href = a.getAttribute('href') || '';
        var key = null;
        if (!have['header-phone'] && href.indexOf('tel:') === 0 && a.closest('header')) key = 'header-phone';
        else if (!have['nav-catalog'] && (t.indexOf('catalog') !== -1) && (a.closest('header') || a.closest('nav'))) key = 'nav-catalog';
        else if (!have['repair-estimate-btn'] && t.indexOf('estimate') !== -1) key = 'repair-estimate-btn';
        else if (!have['fast-pack-btn'] && hasAll(t, ['fast', 'pack'])) key = 'fast-pack-btn';
        else if (!have['repair-order-form'] && hasAll(t, ['repair', 'order', 'form'])) key = 'repair-order-form';
        if (key) { a.setAttribute('data-chat-target', key); have[key] = 1; }
      }
    }

    /* ---------- boot ---------- */
    function boot(data) {
      FAQ = prepare(data);
      fallbackTags();
      dressUp();
      setPose('idle');
      root.classList.add('a1chat-ready');
      scheduleRoam(FIRST_ROAM_MS); // first stroll comes a little sooner than the rest
      scheduleWave();
      if (!REDUCED && !ss(NUDGE_KEY)) {
        setTimeout(function () {
          if (!panelOpen && state === 'parked' && !ss(DISMISS_KEY)) {
            mascot.classList.add('a1chat-wave');
            showBubble(HOLIDAY ? HOLIDAY_TEXT[HOLIDAY][1] : 'Questions? Ask me!', 3400);
            setTimeout(function () { mascot.classList.remove('a1chat-wave'); }, 1600);
            ss(NUDGE_KEY, '1');
          }
        }, NUDGE_DELAY);
      }
      // arrived via a "Show me" link from another page
      if (location.hash.indexOf(HASH_PREFIX) === 0) {
        var key = decodeURIComponent(location.hash.slice(HASH_PREFIX.length));
        try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
        var note = '';
        FAQ.forEach(function (f) { if (f.point_to && f.point_to.target === key && !note) note = f.point_to.note; });
        setTimeout(function () { pointAt(key, note); }, 900);
      }
    }

    if (window.A1_CHAT_FAQ_DATA) boot(window.A1_CHAT_FAQ_DATA);
    else if (faqUrl && window.fetch) {
      fetch(faqUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(boot)
        .catch(function () { boot([]); });
    } else boot([]);

    // exposed for QA in the console / theme preview
    window.A1Chat = {
      match: function (q) { return bestMatches(q, 3).map(function (t) { return [t.f.id, +t.s.toFixed(2)]; }); },
      point: pointAt,
      holiday: function () { return HOLIDAY; },
      holidayFor: function (iso) { return holidayFor(new Date(iso)); },
      closedFor: function (iso) { var p = etParts(new Date(iso)); return closedFor(utc(p.y, p.m, p.d)); },
      isOpenNow: isOpenNow
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
